import { PermissionsService } from '../../core/permissions.service';
import { DevicesService } from '../../services/devices/devices.service';
import { AuthService } from '../../core/auth.service';
import { DSNotification } from '../../models/notification';
import { NotificationsService } from '../../notifications/notifications.service';
import { ColumnsData, FleetColumnData } from '../../models/api/column.model';
import { AppState } from '../../core/state/app-state';
import { VehicleCardService } from './vehicle-card.service';
import { ArchiveService } from '../../archive/archive.service';
import { AppStore } from '../../core/state/app-store';
import { PrintEmptyPipe } from '../pipes/print-empty.pipe';
import { InternalVehicleStatus } from '../../models/vehicle-statuses.model';
import { Groups, Position } from '../../models/api/vehicle-position.model';
import { VehiclesService } from '../../core/vehicles.service';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import { DurationFormatPipe } from '../pipes/duration-format.pipe';
import { isNullOrUndefined } from 'util';
import { DatePipe, DecimalPipe } from '@angular/common';
import { AppSelector } from '../../core/state/app-selector';
import { VehiclesMapService } from '../../vehicles/vehicles-map.service';
import {
  distinctUntilChanged,
  filter,
  finalize,
  map,
  take,
} from 'rxjs/operators';
import { TachoInfo } from './vehicle-card.model';
import { MatDialog } from '@angular/material';
import { PharmacyAlarmDialogComponent } from '../../vehicles/pharmacy-alarm-dialog/pharmacy-alarm-dialog.component';
import { Sensor } from '../../models/sensor';
import Icon from 'ol/style/icon';
import { Pharmacy } from '../../core/pharmacy.service';
import {
  UserVehicleGroup,
  UserVehicleGroupOption,
} from '../../models/vehicle-types.model';
import { StatusIconPipe } from './pipe/icon-status.pipe';
import { isNaN } from 'lodash';

@Component({
  selector: 'app-vehicle-card',
  templateUrl: './vehicle-card.component.html',
  styleUrls: ['./vehicle-card.component.scss'],
  providers: [
    DurationFormatPipe,
    PrintEmptyPipe,
    DecimalPipe,
    AppSelector,
    StatusIconPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VehicleCardComponent implements OnChanges, OnInit {
  @Input() vehicle: Position;
  pharmacy: Pharmacy;
  userVehicleGroups: UserVehicleGroupOption[];

  @Input('pharmacy') set setPharmacy(pharmacy: Pharmacy) {
    if (pharmacy.position) {
      this.pharmacy = pharmacy;
    }

    if (pharmacy && pharmacy.position && pharmacy.position[0]) {
      this.vehicle = pharmacy.position[0];
    }
  }
  @Input()
  set index(index: number) {
    this.ordinalNumber = this.prepareOrdinalNumber(index);
  }
  @Input()
  isMap = false;
  @Input()
  isMapMenu = false;
  @Input()
  isSelected = false;
  @Input()
  showStatus = true;
  @Input()
  onlineData: boolean = true;
  @Input()
  alarmList = [];
  @Output()
  zoomToVehicle = new EventEmitter<Position>();

  // Dialog
  showNoteDialog: boolean = false;
  showEditDialog: boolean = false;
  dialogWidth: string = '700px';

  opened = false;

  fleetColumns: FleetColumnData;
  disableTemperatures: boolean[] = [];

  pharmacyTemperaturesAndHumidityArray: any[];
  lastUpdateTime: number; // millisec
  lastUpdateDuration: number; // millisec
  status: InternalVehicleStatus;
  isExtended: boolean = false;
  driver: string;
  location: string;

  formattedData: {
    baseInfo: any[];
    dailyStatistics: any[];
    computerInfo: any[];
    tachoInfo: TachoInfo[];
    additionalInfo: any[];
    otherInfo: any[];
  } = {
    baseInfo: [],
    dailyStatistics: [],
    computerInfo: [],
    tachoInfo: [],
    additionalInfo: [],
    otherInfo: [],
  };
  onOffColumn: string;
  privateRide: boolean;

  notifications: DSNotification[];
  newEtollNotifications = false;
  ordinalNumber: string;

  constructor(
    public vehiclesService: VehiclesService,
    public devicesService: DevicesService,
    public permissionsService: PermissionsService,
    protected durationPipe: DurationFormatPipe,
    protected datePipe: DatePipe,
    protected selector: AppSelector,
    protected appStore: AppStore,
    protected archiveService: ArchiveService,
    protected vehiclesMapService: VehiclesMapService,
    protected vehicleCardService: VehicleCardService,
    public authService: AuthService,
    public notificationService: NotificationsService,
    public changeRef: ChangeDetectorRef,
    private dialog?: MatDialog
  ) {}

  ngOnInit() {
    this.appStore.state$
      .pipe(
        map((state: AppState) => state.columns),
        filter((columns: ColumnsData) => !!columns.fleetColumns),
        distinctUntilChanged()
      )
      .subscribe((columns) => {
        this.fleetColumns = columns.fleetColumns;
        this.ngOnChanges();
      });

    this.appStore.state$
      .pipe(
        map((state: AppState) => state.filterVehicles.userVehicleGroups),
        filter((userVehicleGroups) => !!userVehicleGroups),
        take(1)
      )
      .subscribe((userVehicleGroups) => {
        this.userVehicleGroups = userVehicleGroups;
        this.ngOnChanges();
      });

    this.notificationService
      .getNotificationPerVehicle(this.vehicle && this.vehicle.vehicle)
      .subscribe((notifications) => {
        this.notifications = notifications;
        if (this.notifications && this.notifications.length > 0) {
          if (
            new Date().getTime() - this.notifications[0].createdAt.getTime() <
            10 * 60 * 1000
          ) {
            this.newEtollNotifications = true;
          }
        }
        this.changeRef.markForCheck();
      });
  }

  ngOnChanges() {
    if (!this.vehicle) {
      return;
    }
    this.lastUpdateTime = this.vehicle.time
      ? // tslint:disable-next-line:radix
        parseInt(this.vehicle.time) * 1000
      : void 0;
    this.lastUpdateDuration = this.whenLastUpdated();
    this.status = this.vehicleCardService.prepareStatus(
      this.vehicle,
      this.lastUpdateDuration
    );
    this.location = this.vehicleCardService.prepareLocation(this.vehicle);
    this.onOffColumn = this.vehicleCardService.prepareOnOff(this.vehicle);

    this.pharmacyTemperaturesAndHumidityArray =
      this.vehicleCardService.getTemperaturesForVehicles(
        this.pharmacy.position
      );

    this.formatData();
    this.changeRef.markForCheck();
  }

  formatData() {
    this.formattedData.baseInfo = this.vehicleCardService.prepareBaseInfo(
      this.vehicle,
      this.fleetColumns
    );
    if (this.opened) {
      if (this.isExtended) {
        this.formattedData.additionalInfo =
          this.vehicleCardService.prepareAdditionalInfo(this.vehicle);
      }
    }
  }

  isPowerLevelColumn(key: string): boolean {
    return [
      'EXT_DSTB16_POWER_LEVEL',
      'EXT_DSTB17_POWER_LEVEL',
      'EXT_DSTB18_POWER_LEVEL',
      'EXT_DSTB19_POWER_LEVEL',
      'EXT_DSTB20_POWER_LEVEL',
    ].includes(key);
  }
  openLocationInGoogleMaps() {
    if (this.onlineData) {
      this.vehiclesMapService.openMap(this.vehicle.vehicle);
    } else {
      this.vehiclesMapService.openWindowWithMap(this.vehicle);
    }
  }

  prepareOrdinalNumber(index: number): string {
    return !isNullOrUndefined(index) ? `${index + 1}.` : '';
  }

  whenLastUpdated(): number {
    const currentTime = new Date().getTime();
    if (this.lastUpdateTime) {
      const timeDiff = currentTime - this.lastUpdateTime;
      return timeDiff > 0 ? timeDiff : 0;
    }
  }

  getStatusIcon(status: string): string {
    let iconName: string;
    switch (status) {
      case InternalVehicleStatus.POWER_CRITICAL:
        iconName = 'battery-empty';
        break;
      case InternalVehicleStatus.POWER_WARNING:
        iconName = 'battery-half yellow';
        break;
      case InternalVehicleStatus.OK:
        iconName = 'check green';
        break;
      case InternalVehicleStatus.DELAYED:
        iconName = 'ellipsis-h';
        break;
      case InternalVehicleStatus.UNAVAILABLE:
        iconName = 'ban';
        break;
      case InternalVehicleStatus.TRANSMISSION_CRITICAL:
        iconName = 'wifi yellow';
        break;
      case InternalVehicleStatus.DROPLET:
        iconName = 'droplet';
        break;
      case InternalVehicleStatus.TEMPERATURE:
        iconName = 'temperature-half';
        break;
      default:
        iconName = 'ban';
        break;
    }
    return `fa fa-${iconName}`;
  }

  getTranslationForStatus(
    status: InternalVehicleStatus,
    forTooltip?: boolean
  ): string {
    const translationPath = `vehicles.vehiclePosition.internalStatuses.${status}`;
    return forTooltip ? translationPath + '.long' : translationPath + '.short';
  }

  showStatusIcon(): boolean {
    return this.status !== InternalVehicleStatus.OK && this.showStatus;
  }

  isStatusCritical(): boolean {
    return (
      this.status === InternalVehicleStatus.POWER_CRITICAL ||
      this.status === InternalVehicleStatus.UNAVAILABLE
    );
  }
  isStatusOk(): boolean {
    return this.status === InternalVehicleStatus.OK;
  }

  isStatusFromToday(time: number) {
    return new Date(time).toDateString() === new Date().toDateString();
  }

  isVehicleWorking(): boolean {
    return (
      this.vehicle.speed !== '255' &&
      (this.status === InternalVehicleStatus.OK ||
        this.status === InternalVehicleStatus.DELAYED ||
        this.status === InternalVehicleStatus.POWER_WARNING)
    );
  }

  toggle() {
    this.opened = !this.opened;
    if (this.opened) {
      this.formatData();
      this.selectVehicle();
    }
  }

  selectVehicle() {
    if (
      this.appStore.getState().selectedVehicle === undefined ||
      this.appStore.getState().selectedVehicle.vehicle !== this.vehicle.vehicle
    ) {
      this.archiveService.setSelectedVehicle(this.vehicle.vehicle);
      this.appStore.selectVehicle(this.vehicle);
    }
  }

  onZoomToVehicle(vehicle: Position) {
    this.zoomToVehicle.emit(vehicle);
  }

  isVehicleHasLocation() {
    return parseFloat(this.vehicle.lon) && parseFloat(this.vehicle.lat);
  }

  onExtend() {
    this.isExtended = !this.isExtended;
    if (this.isExtended) {
      this.formatData();
    }
  }

  showExtendBtn() {
    return (
      this.vehicle &&
      this.vehicle.extcolumns &&
      this.vehicle.extcolumns.ext &&
      this.vehicle.extcolumns.ext.length
    );
  }

  getTranslationForColumns(key: string) {
    return `vehicles.vehiclePosition.columns.${key}`;
  }

  onNoteOpen() {
    this.showNoteDialog = true;
  }

  onEditOpen() {
    this.showEditDialog = true;
  }

  onNoteSave(noteValue: string, vehicleId: string) {
    this.vehiclesService.saveNote(vehicleId, noteValue).subscribe(
      (note) => {
        this.vehicle.note = note;
        this.showNoteDialog = false;
      },
      () => {
        this.showNoteDialog = false;
      }
    );
  }
  onDeviceEdit(change: { description: string }, vehicleId: string): void {
    this.devicesService
      .putDeviceInformation(vehicleId, change)
      .pipe(
        finalize(() => {
          this.showEditDialog = false;
        })
      )
      .subscribe(() => {
        this.appStore.refreshDevicesInformation().subscribe(() => {});
      });
  }

  getNoteIconClass() {
    return this.vehicle.note && this.vehicle.note.value
      ? 'fa fa-sticky-note'
      : 'far fa-sticky-note';
  }

  isParamInDanger(param: any): boolean {
    switch (param.key) {
      // case 'EXT_DSTB16_POWER_LEVEL':
      // case 'EXT_DSTB17_POWER_LEVEL':
      // case 'EXT_DSTB18_POWER_LEVEL':
      // case 'EXT_DSTB19_POWER_LEVEL':
      // case 'EXT_DSTB20_POWER_LEVEL':
      //   return parseInt(param.value, 1) < 20;
      case 'EXT_Stan_Zasil':
        return param.value.match(/(\d+)/)[0] === '0';
      default:
        return false;
    }
  }

  getSensorNameByColumnName(key: string) {
    if (!key) {
      return;
    }
    const delimiter = 'EXT_DST';
    if (key.includes(delimiter)) {
      const index = key.split(delimiter);
      return this.getSensorName(Number(index[1]));
    }
  }

  openAlarmDialog(
    groups: Groups[],
    index: string,
    name: string,
    pharmacyTemperaturesAndHumidity: Sensor[],
    indexSensor: number
  ) {
    const vehicleSensorsIndex = this.pharmacy.position.findIndex((item) => {
      return item.name === name;
    });

    const dialogRef = this.dialog.open(PharmacyAlarmDialogComponent, {
      minWidth: '33%',
      maxWidth: '525px',
      panelClass: 'custom-dialog-container',
      disableClose: true,
      data: {
        groups,
        index,
        name,
        pharmacyTemperaturesAndHumidity,
        sensors: this.pharmacy.position[vehicleSensorsIndex].sensors,
        indexSensor,
        alarmList: this.alarmListForSensor(indexSensor),
        pharmacyName: this.pharmacy.name,
      },
    });

    dialogRef.componentInstance.alarmEnded.subscribe(
      (shouldReopen: boolean) => {
        if (shouldReopen) {
          dialogRef
            .afterClosed()
            .pipe(take(1))
            .subscribe(() => {
              const updatedData = {
                groups,
                index,
                name,
                pharmacyTemperaturesAndHumidity,
                sensors: this.pharmacy.position[vehicleSensorsIndex].sensors,
                indexSensor,
                alarmList: this.alarmListForSensor(indexSensor),
              };
              if (updatedData.alarmList.length > 0) {
                this.openAlarmDialog(
                  groups,
                  index,
                  name,
                  pharmacyTemperaturesAndHumidity,
                  indexSensor
                );
              } else {
                this.changeRef.detectChanges();
              }
            });
        } else {
          this.changeRef.detectChanges();
        }
      }
    );
  }
  getGroup(groups: number[]): string {
    if (groups && this.userVehicleGroups) {
      const result = this.userVehicleGroups
        .filter((item) => groups.includes(item.value.id))
        .map((item) => item.value.name)
        .join(', ');

      return result;
    }
  }

  getSensorName(id: number, pharmacyTemperaturesAndHumidity?: any): string {
    if (
      pharmacyTemperaturesAndHumidity &&
      pharmacyTemperaturesAndHumidity.vehicle.sensors
    ) {
      const sensorItem = pharmacyTemperaturesAndHumidity.vehicle.sensors.find(
        (sensor) => sensor.slot === id
      );
      if (sensorItem) {
        return sensorItem.name;
      }
    }
  }

  /**
   * getMac() – pobiera MAC sensora ze struktury
   * Zwraca pusty string, jeśli brak lub nie ma slotu
   */
  getMac(id: number, pharmacyTemperaturesAndHumidity?: any): string {
    if (
      pharmacyTemperaturesAndHumidity &&
      pharmacyTemperaturesAndHumidity.vehicle &&
      pharmacyTemperaturesAndHumidity.vehicle.sensors
    ) {
      const sensorItem = pharmacyTemperaturesAndHumidity.vehicle.sensors.find(
        (sensor) => sensor.slot === id
      );
      if (sensorItem) {
        return sensorItem.mac;
      }
    }
    return '';
  }

  /**
   * isSensorVisible() – jeśli czujnik jest odłączony i brak MAC-a,
   * to w ogóle chowamy kontener. W przeciwnym razie (lub jeśli jest MAC)
   * to go pokazujemy.
   */
  isSensorVisible(
    sensorId: number,
    pharmacyTah: any,
    temperatureAndHumidity: any
  ): boolean {

    let isDisconnected = false;
    if (
      temperatureAndHumidity &&
      temperatureAndHumidity.temperature &&
      typeof temperatureAndHumidity.temperature.toLowerCase === 'function'
    ) {
      isDisconnected =
        temperatureAndHumidity.temperature.toLowerCase().includes('odłącz');
    }

    const mac = this.getMac(sensorId, pharmacyTah);

    if (isDisconnected && (!mac || mac === '0')) {
      return false;
    }
    return true;
  }


  isAlarm(vehicleId, sensorIndex) {
    return this.alarmList.find(
      (alarm) =>
        alarm.vehicleId.toString() === vehicleId.toString() &&
        alarm.slots.includes(sensorIndex)
    );
  }

  alarmListForSensor(idSensor) {
    return this.alarmList.filter((alarm) => alarm.slots.includes(idSensor));
  }

  trackByVehicleId(
    index: number,
    pharmacyTemperaturesAndHumidity: any
  ): string {
    return pharmacyTemperaturesAndHumidity.vehicle.vehicle;
  }

  trackByTemperatureId(index: number, temperatureAndHumidity: any): string {
    return temperatureAndHumidity.id[0] || index;
  }

  toggleTemperatureVisibility(index: number): void {
    this.disableTemperatures[index] = !this.disableTemperatures[index];
  }

  protected readonly Icon = Icon;
  protected readonly isNaN = isNaN;
  protected readonly parseFloat = parseFloat;
}
